'use client';

import { Navbar } from '@/components/navigation/Navbar';
import { Footer } from '@/components/navigation/Footer';
import { Section } from '@/components/ui/Section';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Lock, Mail } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { isValidEmail } from '@/lib/security';
export default function ConnexionPage() {
  const t = useTranslations('auth.login');
  const tReset = useTranslations('auth.resetPassword');
  const router = useRouter();
  const searchParams = useSearchParams();
  const resetSuccess = searchParams.get('reset') === 'success';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  const fetchCsrfToken = async (): Promise<string | null> => {
    try {
      const response = await fetch('/api/csrf', {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin'
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const token = typeof data?.token === 'string' ? data.token : null;

      if (token) {
        setCsrfToken(token);
      }

      return token;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    void fetchCsrfToken();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!email.trim()) {
      setErrors({ email: t('errorEmail') });
      return;
    }
    if (!isValidEmail(email.trim())) {
      setErrors({ email: t('errorEmailInvalid') });
      return;
    }
    if (!password) {
      setErrors({ password: t('errorPassword') });
      return;
    }

    const tokenToUse = csrfToken || await fetchCsrfToken();
    if (!tokenToUse) {
      setErrors({ general: 'Erreur de sécurité. Veuillez rafraîchir la page.' });
      setStatus('error');
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': tokenToUse
        },
        body: JSON.stringify({ mail: email.trim().toLowerCase(), mdp: password })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setErrors({ general: 'Trop de requêtes. Veuillez réessayer plus tard.' });
        } else if (response.status === 403) {
          setErrors({ general: 'Erreur de sécurité. Veuillez rafraîchir la page.' });
          await fetchCsrfToken();
        } else {
          setErrors({ general: data.error || t('errorLogin') });
        }
        setStatus('error');
        return;
      }

      setStatus('success');
      router.push('/');
    } catch {
      setErrors({ general: t('errorGeneric') });
      setStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <main>
        <Section variant="gradient" className="pt-32 pb-20">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-7xl font-bold leading-normal mb-6 text-navy">
              {t('title')}
            </h1>
            <p className="text-2xl text-foreground max-w-2xl mx-auto">{t('subtitle')}</p>
          </div>

          <div className="max-w-md mx-auto">
            <Card className="bg-white/90">
              <form onSubmit={handleSubmit} noValidate className="space-y-6">
                {resetSuccess && (
                  <div className="p-3 bg-accent/20 border border-accent/40 rounded-lg">
                    <p className="text-foreground text-sm">{tReset('success')}</p>
                  </div>
                )}
                {errors.general && (
                  <div className="p-3 bg-danger/20 border border-danger/40 rounded-lg">
                    <p className="text-danger text-sm">{errors.general}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-foreground mb-2">
                    {t('email')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-accent/70" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email) setErrors({ ...errors, email: '' });
                      }}
                      className={`w-full pl-11 pr-4 py-3 bg-white border rounded-lg text-foreground focus:outline-none transition-colors ${
                        errors.email ? 'border-danger' : 'border-accent/30 focus:border-accent'
                      }`}
                      required
                      maxLength={255}
                      autoComplete="email"
                      disabled={status === 'loading'}
                    />
                  </div>
                  {errors.email && <p className="text-danger text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="password" className="block text-foreground mb-2">
                    {t('password')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-accent/70" />
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) setErrors({ ...errors, password: '' });
                      }}
                      className={`w-full pl-11 pr-4 py-3 bg-white border rounded-lg text-foreground focus:outline-none transition-colors ${
                        errors.password ? 'border-danger' : 'border-accent/30 focus:border-accent'
                      }`}
                      required
                      autoComplete="current-password"
                      disabled={status === 'loading'}
                    />
                  </div>
                  {errors.password && <p className="text-danger text-sm mt-1">{errors.password}</p>}
                </div>

                <div className="text-center">
                  <Link
                    href="/connexion/mot-de-passe-oublie"
                    className="text-accent hover:text-secondary text-sm font-medium transition-colors"
                  >
                    {t('forgotPassword')}
                  </Link>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? t('loading') : t('submit')}
                </Button>
              </form>
            </Card>
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
